import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import * as yup from "yup";
import {
  Dashboard,
  Receipt,
  Add,
  MoreHoriz,
  Edit,
  Delete,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";

import PageContainer from "@jumbo/components/PageContainer";
import Table from "@jumbo/components/JumboTable";
import Dropdown from "@jumbo/components/JumboDdMenu";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";

import { BILL_ADD, HOME, createBillEditRoute } from "@jumbo/constants/routes";
import { deleteBill, getBillList } from "app/redux/slice/billSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import moment from "moment";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { getPartiList } from "app/redux/slice/partiSlice";
import { partiType } from "@jumbo/constants/parti";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { userType } from "app/helpers/userType";

const useStyles = makeStyles({
  buttonPadding: {
    width: "fit-content",
    marginTop: "20px",
    marginRight: "12px",
  },
  btnContainer: {
    display: "flex",
    justifyContent: "end",
    alignItems: "center",
    marginBottom: "20px",
  },
  dropDownContainer: {
    padding: "20px 12px 0px 12px",
  },
  filterContainer: {
    marginBottom: "30px",
  },
});

const breadcrumbData = [
  {
    path: HOME,
    icon: <Dashboard />,
    label: "Dashboard",
  },
  {
    icon: <Receipt />,
    label: "Bill",
  },
];

const validationSchema = yup.object().shape({
  toDate: yup.date().when("fromDate", (fromDate, schema) => {
    if (fromDate) {
      return schema
        .required("To Date is required")
        .min(fromDate, "To Date cannot be before From Date");
    }
    return schema;
  }),
});

const Bill = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const downloadDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const dispatch = useDispatch();
  const { isMA } = userType();
  const { enqueueSnackbar } = useSnackbar();
  const billData = useSelector((state) => state?.bill);
  const partiData = useSelector((state) => state?.parti);
  const [page, setPage] = useState(0);
  const [id, setId] = useState();
  const [vendor, setVendor] = useState();

  const [rowsPerPage, setRowsPerPage] = React.useState(
    process.env.REACT_APP_PER_PAGE ?? 10
  );
  const [permissionData] = useState(JSON.parse(localStorage.getItem('permission')));

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  const actionMenu = [
    { icon: <Edit />, title: "Edit", action: "edit", disabled: !permission.updatePermission },
    { icon: <Delete />, title: "Delete", action: "delete", disabled: !permission.deletePermission },
  ];

  const getPermission = () => {
    if (isMA) {
      setPermission({ createPermission: true, updatePermission: true, deletePermission: true });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes('Bill') && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === 'bill-create') {
            permissionValue.createPermission = true;
          }
          if (num?.name === 'bill-delete') {
            permissionValue.deletePermission = true;
          }
          if (num?.name === 'bill-update') {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const column = [
    {
      id: "date",
      label: "Date",
      render: (elm) => <> {moment(elm?.date).format("DD/MM/YYYY")}</>,
    },
    { id: "billNo", label: "Bill No" },
    { id: "vendor", label: "Vendor" },
    { id: "amount", label: "Amount" },
    {
      label: "Paid Amount",
      render: (elm) => {
        return elm.amount - elm.pendingAmount;
      },
    },
    { id: "pendingAmount", label: "Pending Amount" },
    {
      label: "Actions",
      key: "actionIcon",
      sort: false,
      render: (elm) => {
        return (
          <Dropdown
            icon={<MoreHoriz />}
            menuItems={actionMenu}
            onClickCallback={(event) => handleItemAction(event, elm)}
          />
        );
      },
    },
  ];

  const handleItemAction = async (menuItem, record) => {
    switch (menuItem.action) {
      case "edit":
        navigate(createBillEditRoute(record?._id));
        break;
      case "delete":
        setId(record._id);
        deleteDisclosure.onOpen();
        break;
      default:
        break;
    }
  };

  const getData = () => {
    const value = {
      page: page + 1,
      perPage: rowsPerPage,
      search: values?.search?.trim(),
      fromDate: values?.fromDate,
      toDate: values?.toDate,
    };
    if (vendor) value.vendorId = vendor._id;
    dispatch(getBillList(value));
  };
  const formik = useFormik({
    initialValues: {
      fromDate: "",
      toDate: "",
      search: "",
    },
    validateOnChange: false,
    validationSchema: validationSchema,
    onSubmit: getData,
  });
  const handleConfirm = () => {
    dispatch(deleteBill(id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          deleteDisclosure.onClose();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch]);
  useEffect(() => {
    dispatch(getPartiList({ partiType: partiType.Vendor }));
  }, [dispatch]);
  const { values, handleSubmit } = formik;
  return (
    <PageContainer heading="Bill" breadcrumb={breadcrumbData}>
      <Paper className={classes?.filterContainer}>
        <Card sx={{ display: "flex" }}>
          <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
            <CardContent>
              <FormikProvider value={formik}>
                <Form noValidate autoComplete="off">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <JumboTextField
                        fullWidth
                        size="small"
                        name="search"
                        label="Search"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <JumboAutoComplete
                        options={partiData.list}
                        label="Vendor"
                        size="small"
                        getOptionLabel={(option) => option.name}
                        name="vendor"
                        onChange={(evt, value) => setVendor(value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <JumboTextField
                        type="date"
                        label={"From Date"}
                        name="fromDate"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        value={values?.fromDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <JumboTextField
                        type="date"
                        label={"To Date"}
                        name="toDate"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        value={values?.toDate}
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        onClick={() => {
                          setPage(0);
                          handleSubmit();
                        }}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Div>
        </Card>
      </Paper>
      <Paper>
        {permission.createPermission && <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<Add />}
            className={classes?.buttonPadding}
            onClick={() => navigate(BILL_ADD)}
          >
            Add
          </Button>
        </Box>}
        <Table
          columns={column}
          rows={billData.list}
          count={billData.total}
          setPage={setPage}
          page={page}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          isBill
        />
      </Paper>
      <DialogConfirm
        open={downloadDisclosure?.isOpen}
        title="Download Bill"
        message="Are you sure you want to download this Bill ?"
        onConfirm={() => downloadDisclosure.onClose()}
        onClose={() => downloadDisclosure.onClose()}
      />

      <DialogConfirm
        open={deleteDisclosure?.isOpen}
        title="Delete Bill"
        message="Are you sure you want to delete this Bill ?"
        onConfirm={handleConfirm}
        onClose={deleteDisclosure.onClose}
      />
    </PageContainer>
  );
};

export default Bill;
